import React, { useEffect, useState } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Theme,
} from "@mui/material";
import { Field } from "../../utils/formSchema";
import { batchApi } from "../../services/settings/batch";
import CustomInputButton from "../../components/Input/customInputButton";
import DamsTable from "../../components/Table/CollapsibleTable";
import { TRowItem } from "../../components/Table/TableRow";
import { useMangeTableItems } from "../../hooks/useManageTableItems";
import { usePagination } from "../../hooks/usePagination";
import { useDmsForm } from "../../hooks/useDmsFrom";
import { DatePicker } from "../../components/calender";
import { FilterModal } from "../FilterModal";

import { ContainedButton, TextButton } from "../../components/Button/AppButton";

import { FilterItemButton } from "../../components/Button/FilterItemButton";
import { IBatch, IPagination } from "../../config/types";
import { SideBarSearchInput } from "../../components/Layout/SideBarSearchInput";
import { formatSnakeCaseToReadable } from "../../utils/common";

interface InputProps {
  placeholder: string;
  label: string;
  handleChange: (item: Field, label?: string) => void;
  value?: string;
  size?: "full" | "short" | "long";
  error?: boolean;
  height?: "small" | "normal";
  errorMessage?: string;
  required?: boolean;
}

interface Ifilter {
  batch_number: string;
  donor: string;
  received_date: string;
}
export const BatchSelectInput = ({
  placeholder,
  handleChange,
  label,
  error,
  value,
  size = "full",
  height = "normal",
  errorMessage = "required",
  required = false,
}: InputProps) => {
  const [dataset, setDataset] = useState<TRowItem[]>([]);
  const [selectName, setSelectName] = useState("");
  const [loading, setLoading] = useState(true);
  const { selectedRowItems, toggleRow } = useMangeTableItems(dataset, false);
  const [querySet, setQuerySet] = useState({ is_deleted: false });
  const {
    pagination,
    paginateToNextPage,
    paginateToPreviousPage,
    paginateToPageNumber,
    updatePaginationInitState,
  } = usePagination<any>(batchApi.getBatches);

  const { form, updateInputField, updateForm, onFormSubmit, getFormValues } =
    useDmsForm<Ifilter>({
      batch_number: "",
      donor: "",
      received_date: "",
    });

  const populateDataSet = (data: IBatch[], pagination?: IPagination) => {
    setDataset(data);
    updatePaginationInitState(pagination);
  };
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const res = await batchApi.getBatches({ is_deleted: false });
      if (res.data) {
        populateDataSet(res.data, res.pagination);
        if (value) {
          const __default = (await batchApi.getBatch(value)).data;
          if (__default) {
            setSelectName(__default.batch_number);
            handleChange(
              { value: __default.id, isValid: true },
              __default.batch_number
            );
            toggleRow(__default as TRowItem);
          }
        }
      }
      setLoading(false);
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [open, setOpen] = React.useState(false);
  const toggleModel = () => {
    setOpen(!open);
  };
  const handleClose = () => {
    setOpen(false);
  };
  const handleDatePicker = (date: string) => {
    updateForm((p) => ({
      ...p,
      received_date: { value: date, isValid: true },
    }));
  };

  const resetFilter = <K extends keyof Ifilter>(key: K) => {
    updateForm((p) => {
      const state = {
        ...p,
        [key]: { value: "" },
      };
      setQuerySet((prev) => ({ ...prev, [key]: "" }));
      return state;
    });
  };

  useEffect(() => {
    const { data } = onFormSubmit();
    setQuerySet((p) => ({ ...p, ...data }));

    const filterRequest = async () => {
      setLoading(true);
      const res = await batchApi.getBatches({ is_deleted: false, ...data });
      if (res.data) {
        populateDataSet(res.data, res.pagination);
      }
      setLoading(false);
    };
    filterRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.batch_number.value, form.received_date.value, form.donor.value]);

  const handleUserResponse = (row: TRowItem) => {
    const response = toggleRow(row);
    if (response && response.isSelected) {
      handleChange(
        {
          value: row?.id ?? "",
          isValid: row?.id ? true : false,
        },
        row?.batch_number
      );
      setSelectName(row?.batch_number);
      handleClose();
    }
  };
  return (
    <Box>
      <CustomInputButton
        loading={loading}
        label={label}
        placeholder={placeholder}
        value={selectName}
        handleChange={toggleModel}
        error={error}
        errorMessage={errorMessage}
        size={size}
        height={height}
        required={required}
      />
      <Dialog
        maxWidth={"xl"}
        open={open}
        sx={{ height: "100%" }}
        onClose={toggleModel}
      >
        <Stack
          direction={"row"}
          justifyContent={"space-between"}
          paddingRight={2}
        >
          <DialogTitle fontWeight={600}>{"Batch Identifier"}</DialogTitle>
          {value === "" ? (
            <Box marginTop={2}>
              <TextButton title="Continue" />
            </Box>
          ) : (
            <DialogActions>
              <ContainedButton onClick={handleClose} title="Continue" />
            </DialogActions>
          )}
        </Stack>

        <DialogContent sx={styles.content}>
          <Box sx={styles.filterBox}>
            <Stack direction={"row"} spacing={2}>
              <FilterModal label="Filters" loading={loading}>
                <Stack direction={"column"} spacing={2}>
                  <DatePicker
                    handleOnchange={handleDatePicker}
                    label="Date Recieved"
                  />
                  <SideBarSearchInput
                    label="Donor Name"
                    placeholder="Search by donor name..."
                    value={form.donor.value}
                    handleChange={updateInputField("donor")}
                  />
                  <SideBarSearchInput
                    label="Batch Number"
                    placeholder="Search by batch number..."
                    value={form.batch_number.value}
                    handleChange={updateInputField("batch_number")}
                  />
                </Stack>
                <Box></Box>
              </FilterModal>

              {Object.entries(getFormValues())
                .filter(
                  ([_, value]) =>
                    typeof value === "string" && value.trim() !== ""
                )
                .map(([key, value]) => (
                  <FilterItemButton
                    key={key}
                    label={formatSnakeCaseToReadable(key)}
                    tip={value}
                    height={40}
                    onClick={() => resetFilter(key as keyof Ifilter)}
                  />
                ))}
            </Stack>
          </Box>

          <DamsTable
            isModel
            tableHeaders={columns}
            tableKeys={tkeys}
            hiddenKeys={hkeys}
            hiddenLabels={hcolumns}
            data={dataset}
            hasHeaderCheckBox={false}
            hasPagination
            paginationState={pagination}
            onNextPage={paginateToNextPage(querySet, setDataset)}
            onPreviousPage={paginateToPreviousPage(querySet, setDataset)}
            setActivePage={paginateToPageNumber(setDataset)}
            useTableCheckbox
            onTableItemSelect={handleUserResponse}
            selectedRowItems={selectedRowItems}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

const styles = {
  filterBox: {
    width: "100%",
    marginBottom: 5,
  },
  content: (theme: Theme) => ({
    height: "100vh",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    [theme.breakpoints.up("xl")]: {
      width: "70vw",
    },
    width: "90vw",
  }),
  table: {
    width: "100vw",
    height: "70%",
  },
};
const tkeys = ["donor", "batch_domain", "batch_number", "received_date"];

const columns = ["Donor", "Batch Domain", "Batch Number", "Received Date"];

const hkeys = ["remarks", "updated_at"];

const hcolumns = ["Remarks", "Last updated At"];
